import { Injectable, OnDestroy, ApplicationRef, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { takeUntil, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { CancelService } from './cancel.service';
import { PollCompletionService } from './poll-completion.service';
import { SpinnerService } from '../../../app/shared/services/spinner.service';
import { environment } from '../../../environments/environment';

/**
 * Interface for polling status request
 */
interface DeferredRequestStatusRequest {
  tracingId: string;
  domain: string;
}

/**
 * Interface for polling status response
 * Aligned with server-side DeferredRequestStatusResponse DTO
 */
interface DeferredRequestStatusResponse {
  tracingId: string;
  domain: string;
  status: string;
  isCompleted: boolean;
  isNotFound: boolean;
  processorStates: { [key: string]: string } | null;
  requiredProcessors: string[];
  pendingProcessors: string[];
  completedProcessors: string[];
  message: string;
  initializedAt: string | null;
}

/**
 * Interface for tracking active polling operations
 */
interface ActivePoll {
  traceId: string;
  domain: string;
  startTime: number;
  timeoutId?: any;
  pollUrl?: string; // Optional custom poll URL
}

/**
 * PollingService - Manages HTTP-based polling operations with automatic header detection
 * 
 * This service provides centralized polling logic that:
 * - Polls at configurable intervals (first poll 2s, subsequent 3s)
 * - Enforces maximum polling duration (3 minutes)
 * - Manages spinner state manually (bypasses global interceptor)
 * - Handles errors gracefully (retries on error)
 * - Cancels on navigation events
 * - Broadcasts completion events
 */
@Injectable({
  providedIn: 'root'
})
export class PollingService implements OnDestroy {
  // Configuration - Can be modified as needed
  private readonly POLL_URL = `${environment.urls.apis.documents}DeferredRequestStatus/status`;
  private readonly FIRST_POLL_DELAY = 2000;      // 2 seconds
  private readonly POLL_INTERVAL = 3000;         // 3 seconds
  private readonly MAX_POLL_DURATION = 180000;   // 3 minutes

  // Track active polling operations by traceId
  private activePolls = new Map<string, ActivePoll>();

  constructor(
    private http: HttpClient,
    private cancelService: CancelService,
    private pollCompletionService: PollCompletionService,
    private spinnerService: SpinnerService,
    private appRef: ApplicationRef,
    private ngZone: NgZone
  ) {}

  /**
   * Starts a polling operation for the given trace ID and domain
   * @param traceId The request tracing identifier
   * @param domain The request domain
   * @param pollUrl Optional custom poll URL (if not provided, uses default POLL_URL)
   */
  startPolling(traceId: string, domain: string, pollUrl?: string): void {
    // Prevent duplicate polling for the same traceId
    if (this.activePolls.has(traceId)) {
      return;
    }


    // Register active poll
    const activePoll: ActivePoll = {
      traceId,
      domain,
      startTime: Date.now(),
      pollUrl: pollUrl // Store optional custom poll URL
    };
    this.activePolls.set(traceId, activePoll);

    // Show spinner manually with message (polling requests bypass global interceptor)
    this.ngZone.run(() => {
      this.spinnerService.forceShow('Your request is being processed. This usually takes a few seconds.');
      this.appRef.tick();
    });

    // Schedule first poll after initial delay
    this.schedulePoll(traceId, domain, this.FIRST_POLL_DELAY);
  }

  /**
   * Schedules the next poll after a specified delay
   * Uses setTimeout (not setInterval) for better control
   */
  private schedulePoll(traceId: string, domain: string, delay: number): void {
    const activePoll = this.activePolls.get(traceId);
    if (!activePoll) {
      return; // Poll was cancelled
    }

    const timeoutId = setTimeout(() => {
      this.executePoll(traceId, domain);
    }, delay);

    activePoll.timeoutId = timeoutId;
  }

  /**
   * Executes a single poll request
   */
  private executePoll(traceId: string, domain: string): void {
    const activePoll = this.activePolls.get(traceId);
    if (!activePoll) {
      return; // Poll was cancelled
    }

    // Check if max duration exceeded
    const elapsed = Date.now() - activePoll.startTime;
    if (elapsed >= this.MAX_POLL_DURATION) {
      this.handleTimeout(traceId, domain);
      return;
    }

    // Create headers to bypass global spinner interceptor
    const headers = new HttpHeaders({
      'X-IGNORE-LOADING': 'true'
    });

    // Use custom poll URL if provided, otherwise use default
    const pollUrl = activePoll.pollUrl || this.POLL_URL;

    // Make poll request with correct body structure
    const body: DeferredRequestStatusRequest = { 
      tracingId: traceId, 
      domain: domain 
    };
    
    this.http.post<DeferredRequestStatusResponse>(pollUrl, body, { headers })
      .pipe(
        takeUntil(this.cancelService.cancel$),
        catchError(error => {
          // Return empty response to continue polling on error
          return of({ 
            tracingId: traceId,
            domain: domain,
            status: 'Error',
            isCompleted: false, 
            isNotFound: false,
            processorStates: null,
            requiredProcessors: [],
            pendingProcessors: [],
            completedProcessors: [],
            message: 'Error occurred',
            initializedAt: null
          } as DeferredRequestStatusResponse);
        })
      )
      .subscribe({
        next: (response) => {
          // Check if request was not found (expired or invalid)
          if (response.isNotFound) {
            // Hide spinner and stop polling since request is not found
            this.handleNotFound(traceId, domain);
            return;
          }

          // Check if poll should stop (operation completed)
          if (response.isCompleted) {
            this.handleCompletion(traceId, domain, response);
          } else {
            // Continue polling - schedule next poll
            const pendingProcessorsInfo = response.pendingProcessors && response.pendingProcessors.length > 0
              ? ` (Pending: ${response.pendingProcessors.join(', ')})`
              : '';
            this.schedulePoll(traceId, domain, this.POLL_INTERVAL);
          }
        },
        error: () => {
          // This should not occur due to catchError, but handle defensively
          this.handleError(traceId, domain);
        }
        // NOTE: Removed 'complete' callback - it was incorrectly stopping polling after first HTTP request
        // The 'complete' callback fires when HTTP request completes normally, NOT just on cancellation
        // takeUntil will handle cancellation by unsubscribing, which prevents further polls from being scheduled
      });
  }

  /**
   * Handles timeout scenario when max polling duration is exceeded
   */
  private handleTimeout(traceId: string, domain: string): void {
    console.warn(`Polling timeout exceeded for traceId: ${traceId}, domain: ${domain}`);
    
    // Stop polling and hide spinner
    this.stopPolling(traceId, true);

    // Broadcast timeout event to notify components inside Angular zone
    this.ngZone.run(() => {
      this.pollCompletionService.emit({
        traceId,
        domain,
        data: null,
        status: 'timeout',
        message: 'The operation timed out after 3 minutes. Please try again or contact support if the issue persists.'
      });
    });
  }

  /**
   * Handles scenario when request is not found (expired or invalid)
   */
  private handleNotFound(traceId: string, domain: string): void {
    console.warn(`Request not found for traceId: ${traceId}, domain: ${domain}`);
    
    // Stop polling and hide spinner
    this.stopPolling(traceId, true);

    // Broadcast not-found event to notify components inside Angular zone
    this.ngZone.run(() => {
      this.pollCompletionService.emit({
        traceId,
        domain,
        data: null,
        status: 'not-found',
        message: 'The request was not found. It may have expired or been cancelled.'
      });
    });
  }

  /**
   * Handles error scenario during polling
   */
  private handleError(traceId: string, domain: string): void {
    console.error(`Polling error for traceId: ${traceId}, domain: ${domain}`);
    
    // Stop polling and hide spinner
    this.stopPolling(traceId, true);

    // Broadcast error event to notify components inside Angular zone
    this.ngZone.run(() => {
      this.pollCompletionService.emit({
        traceId,
        domain,
        data: null,
        status: 'error',
        message: 'An error occurred while processing your request. Please try again.'
      });
    });
  }

  /**
   * Handles successful poll completion
   */
  private handleCompletion(traceId: string, domain: string, data: any): void {
    // Stop polling and cleanup
    this.stopPolling(traceId, true);

    // Broadcast completion event inside Angular zone to ensure change detection runs
    this.ngZone.run(() => {
      this.pollCompletionService.emit({
        traceId,
        domain,
        data,
        status: 'completed'
      });
    });
  }

  /**
   * Stops polling for a specific traceId
   * @param traceId The trace ID to stop polling for
   * @param hideSpinner Whether to hide the spinner
   */
  private stopPolling(traceId: string, hideSpinner: boolean = true): void {
    const activePoll = this.activePolls.get(traceId);
    if (activePoll) {
      // Clear scheduled timeout
      if (activePoll.timeoutId) {
        clearTimeout(activePoll.timeoutId);
      }

      // Remove from active polls
      this.activePolls.delete(traceId);

      // Hide spinner if no other polls are active
      if (hideSpinner && this.activePolls.size === 0) {
        this.ngZone.run(() => {
          this.spinnerService.hide();
          this.appRef.tick();
        });
      }
    }
  }

  /**
   * Cancels all active polling operations
   * Called on navigation or application cleanup
   */
  cancelAllPolls(): void {
    
    // Clear all timeouts
    this.activePolls.forEach((poll) => {
      if (poll.timeoutId) {
        clearTimeout(poll.timeoutId);
      }
    });

    // Clear map
    this.activePolls.clear();

    // Force hide spinner
    this.ngZone.run(() => {
      this.spinnerService.hide();
      
      // Trigger change detection to ensure spinner disappears immediately
      this.appRef.tick();
    });
  }

  /**
   * Gets the count of currently active polls
   */
  getActivePollCount(): number {
    return this.activePolls.size;
  }

  ngOnDestroy(): void {
    this.cancelAllPolls();
  }
}
